import { Controller } from '@hotwired/stimulus';
import { postRequest } from '../../src/utils';

// Used in: app/views/schools/switch_students.html.erb
// HTML structure: Switch students > Band overview > Band space. These are shared with assign students and regular timetable.
// Purpose: Switch students within a band, or between bands, or between timetables
// Explanation: When selecting a band space, first uses 'activePair' which is then added to 'switchedVaue'. On save, sends an array of students (object pairs) to be updated to the backend.

export default class extends Controller {
  static targets = [
    'saveDiscardBtns', // button container div
    'saveBtn', // shown when there are switches
    'savingBtn', // displayed upon patch request
    'notice', // Show which switches were made
  ];

  static values = {
    activePair: Array, // currently selected band spaces
    switched: Array, // array of pairs of all students to be switched
    nextColorIndex: Number, // index of the next color to be used
    switchTotal: { type: Number, default: 1 },
    colors: {
      type: Array,
      default: [
        'blue',
        'red',
        'teal',
        'indigo',
        'yellow',
        'pink',
        'orange',
        'green',
        'fuchsia',
        'violet',
        'lime',
        'sky',
        'rose',
        'emerald',
        'zinc',
        'amber',
        'stone',
        'cyan',
      ],
    },
  };

  connect() {
    console.log('switch students controller connected');
  }

  switchedValueChanged() {
    // When the switch value changes, show the save and discard buttons and a notice of the switches made
    if (this.switchedValue.length > 0) {
      this.saveDiscardBtnsTarget.classList.remove('hidden');
      this.noticeTarget.classList.remove('hidden');
      let html = ``;
      this.switchedValue.forEach((pair, i) => {
        console.log(pair);
        let message = `<strong>Switch ${i + 1}: </strong>
          ${pair[0].studentName},
          Status: ${pair[0].studentStatus},
          Y${pair[0].studentYearGroup},
          Instrument: ${pair[0].studentInstrument},
          Band: ${pair[0].band},
          Timetable: ${pair[0].timetable} - (${pair[0].bandSpaceId} ${
          pair[0].studentId
        })
          <svg fill="none" class="h-5 w-5 mx-2 flex-shrink-0" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"></path>
          </svg>
          ${pair[1].studentName ? pair[1].studentName : 'Empty Space'}, 
          Status: ${pair[1].studentStatus ? pair[1].studentStatus : ' N/A'},
          Y${pair[1].studentYearGroup ? pair[1].studentYearGroup : ' N/A'},
          Instrument: ${pair[1].studentInstrument},
          Band: ${pair[1].band},
          Timetable: ${pair[1].timetable} - (${pair[1].bandSpaceId} ${
          pair[1].studentId
        })`;
        html += `<p class="capitalize flex justify-center flex-col items-center">${message}</p>`;
      });
      this.noticeTarget.innerHTML = html;
    } else {
      this.saveDiscardBtnsTarget.classList.add('hidden');
      this.noticeTarget.classList.add('hidden');
    }
  }

  select(event) {
    // On select of a band space, get the data, check if it's a student or empty space or if it's already selected or if it has already been switched & prevent selection. If it's a legit space, add it to the active pair array and add style.

    // Get bandspace information from event target dataset
    let {
      bandSpaceId,
      studentId,
      studentName,
      studentStatus,
      studentYearGroup,
      timetable,
      band,
      doubleBooked,
      studentInstrument,
    } = event.currentTarget.dataset;

    const isStudent = studentId !== '';
    const firstSelect = this.activePairValue.length == 0;
    const isLeaving = studentStatus === 'Leaving';
    const sameStudent = this.activePairValue[0]?.studentId === studentId;
    const switchToDoubleBookedSpace = !firstSelect && doubleBooked == 'true';

    // Prevent user from selecting empty spaces first
    if (!isStudent && firstSelect)
      return window.alert('Please first select a student to switch');

    // Prevent switching of leaving students
    if (isLeaving) return window.alert('Leaving students can not be switched');

    // Prevent switching already switched students
    let alreadySelected = this.switchedValue
      .flat()
      .some((item) => item.bandSpaceId === bandSpaceId);
    if (alreadySelected) return window.alert('This  has already been switched');

    // Prevent switching into a bandspace with a leaving student
    if (switchToDoubleBookedSpace && isStudent && !sameStudent)
      return window.alert(
        'Can only switch students from double booked spaces into an empty space to prevent two students from going to lessons at the same time and first lesson dates being missed'
      );

    // Set an item that is to be added to the activePair
    let activeItem = {
      isStudent,
      studentId,
      bandSpaceId,
      studentName,
      studentStatus,
      studentYearGroup,
      timetable,
      doubleBooked,
      band,
      studentInstrument,
    };

    // Check if Item has been already added to the active pair
    let itemInActivePair = this.activePairValue.some(
      (item) => item.bandSpaceId === bandSpaceId
    );

    // Add Item to active pair if it is not there yet or remove it if it is there
    let color = this.colorsValue[this.nextColorIndexValue];

    if (!itemInActivePair) {
      this.addToActivePair(activeItem);
      // Set the color of the bandSpace after it gets selected
      event.currentTarget.classList.add(`switch-${color}`);
      event.currentTarget.classList.remove('border-2');
    } else {
      this.clearActivePair();
      // Remove the color of the bandSpace if it's been selected again
      event.currentTarget.classList.remove(`switch-${color}`);
    }

    // Add pair to switched value once two legitimate spaces have been selected
    if (this.activePairValue.length == 2) this.switch();
  }

  addToActivePair(activeItem) {
    let activePair = this.activePairValue;
    activePair.push(activeItem);
    this.activePairValue = activePair;
  }

  clearActivePair() {
    this.activePairValue = [];
  }

  switch() {
    // Add the active pair to the switched value and increment switchedTotalValue

    // Add active pair value to switched array
    let switched = this.switchedValue;
    switched.push(this.activePairValue);
    this.switchedValue = switched;
    let context = this;
    this.activePairValue.forEach((space) => this.styleOnSwitch(space, context));
    context.nextColorIndexValue += 1;
    if (context.nextColorIndexValue === context.colorsValue.length - 1)
      context.nextColorIndexValue = 0;

    context.switchTotalValue += 1;

    // Clear the active pair value
    this.activePairValue = [];
  }

  styleOnSwitch(space, context) {
    // For each of the spaces in the active pair value add color
    let color = context.colorsValue[context.nextColorIndexValue];
    let target = document.getElementById(
      `switch-band-space-${space.bandSpaceId}${space.studentId}`
    );
    target.classList.add(`switch-${color}`);

    // Find the spaces partner space
    let anotherSpace = context.activePairValue.find(
      (item) => item.bandSpaceId !== space.bandSpaceId
    );

    let studentName = anotherSpace.studentName
      ? `${anotherSpace.studentName} (Y${anotherSpace.studentYearGroup})`
      : '';

    // Add the partner space's student name inside the html
    let newHtml = `
        <div class="h-full w-full flex items-center">
          <div class="flex flex-col justify-center items-center w-full h-full mb-auto rounded-md">
            <!-- Student status -->
            <div class="grow-0 w-full flex items-center justify-center text-xs rounded-t p-1">
              <span class="mb-2 text-center">
                ${anotherSpace.studentStatus}
              </span>
            </div>
            <!-- Student name (link)  -->
            <div class="flex grow w-full text-center px-1 py-2 h-full justify-center items-center text-sm leading-4">
              ${studentName}
            </div>
            <!-- Assign students 'x' to cancel -->
            <div class="w-full flex grow-0 p-1 items-center justify-between">
              <div class="flex w-full space-x-1 justify-center items-center">
                <svg
                  class='w-3 h-3'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                  xmlns='http://www.w3.org/2000/svg'>
                  <path
                  stroke-linecap='round'
                  stroke-linejoin='round'
                  stroke-width='2'
                  d='M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4'>
                  </path>
                </svg>
                <span>${context.switchTotalValue}</span>
              </div>
            </div>
          </div>
        </div>`;
    target.innerHTML = newHtml;
  }

  async save() {
    // On save, send the switched value to the controller and reload the page
    console.log("student's to be switched", this.switchedValue);
    console.log("# student's to be switched", this.switchTotalValue);
    this.saveBtnTarget.classList.add('hidden');
    this.savingBtnTarget.classList.remove('hidden');
    console.log('this.switchedValue', this.switchedValue);
    let data = this.prepareSwitchedData();

    console.log('data', data);
    try {
      let res = await this.saveChanges(data);
      if (res.success) {
        window.location.reload();
      } else {
        window.alert('Something went wrong, please try again later');
      }
    } catch (error) {
      window.alert('Something went wrong, please try again later');
    }
  }

  async saveChanges(data) {
    try {
      let resp = await postRequest(`/admin/students/switch_students`, {
        switched: data,
      });
      // On success, reload window
      // On fail, show error message
      let res = await resp.json();
      console.log('res', res);
      return res;
    } catch (error) {
      throw error;
    }
  }

  prepareSwitchedData() {
    return (
      this.switchedValue
        // Swap the bandSpaceId's of the pair
        .map((pair) => {
          [pair[0].bandSpaceId, pair[1].bandSpaceId] = [
            pair[1].bandSpaceId,
            pair[0].bandSpaceId,
          ];
          return pair;
        })
        // Flatten the array
        .reduce((result, pair) => {
          result.push(...pair);
          return result;
        }, [])
        // remove empty band spaces
        .filter((item) => item.isStudent)
        // Map to the correct format { student_id: 1, band_space_id: 1}
        .map((item) => {
          return {
            student_id: item.studentId,
            band_space_id: item.bandSpaceId,
          };
        })
    );
  }

  discard() {
    console.log('discard');
    location.reload();
  }
}
